import { createContext, useContext, useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  getImpersonationToken,
  setImpersonation,
  clearImpersonation,
  decodeImpersonationMeta,
  IMPERSONATION_KEY,
} from "@/lib/impersonation";
// PR TIER1-10 (2026-06-27) — wire PostHog. The SDK has been loaded since
// PR #71 but no events were ever fired. Identify on login + boot, reset on
// logout, and fire a discrete login event so we can build a real
// daily-active-user dashboard.
import { identifyUser, resetUser, trackLogin } from "@/lib/analytics";
// HF-Session-Leak-1 (2026-07-31) — tear down user-scoped localStorage and the
// service worker's API/document cache whenever the identity behind the session
// changes. See lib/sessionCleanup.js for what was leaking and why.
import { clearSessionData } from "@/lib/sessionCleanup";
// STAFF-SESSIONS-1 (30 Oct 2026) — 12-hour staff tokens, renewed while active.
import { shouldRenew, CHECK_EVERY_MS } from "@/lib/staffSession";
import { toast } from "sonner";

const AuthContext = createContext(null);

// Tracks the last identity this browser saw, so we can wipe user-scoped data
// when a DIFFERENT person signs in — the case a logout-only cleanup misses
// (shared machine, previous user just closed the tab without signing out).
const LAST_USER_KEY = "solara.lastUserId";

// HF-Session-Leak-2 (2026-09-16) — separate from LAST_USER_KEY above, and
// deliberately sessionStorage (per-tab, cleared when the tab/window closes)
// rather than localStorage. Records that the CURRENT identity was
// established by an explicit credential action — password/magic-link/
// passkey login, or a fresh registration — taken in this tab this session,
// as opposed to `user` simply being populated because fetchUser() found an
// already-valid token sitting in localStorage when the page loaded.
//
// Why this matters: a borrower's JWT lives in localStorage for hours (see
// config.JWT_EXPIRATION_HOURS_CLIENT) and survives closing the TAB but not
// the underlying login. On a shared/walk-in device, a borrower who
// finished a browsing session without clicking "log out" left that
// browser silently authenticated as them. The next, unrelated person to
// open the site in a NEW tab got auto-signed-in as that borrower and,
// on /apply, straight into their in-progress application — full PII,
// no login prompt ever shown. sessionStorage is the right primitive here
// specifically because it does NOT survive a new tab/window, so the
// marker is gone exactly when a different physical person is the most
// likely explanation for the tab being new.
//
// See LoanApplicationForm's identity-confirmation gate for the consumer:
// it refuses to hydrate a previous draft into the form until either this
// marker matches the current user, or the person explicitly confirms
// "yes, continue as me" (which sets it) or re-authenticates.
const FRESH_AUTH_KEY = "solara.freshAuthUserId";

// STAFF-MFA-1 (30 Oct 2026) — "remember this device" token. Survives sign-out
// on purpose (that is what remembering means); sign out everywhere, a password
// change or a second-factor reset makes the server forget it.
export const TRUSTED_DEVICE_KEY = "solara.trustedDevice";

function readFreshAuthUserId() {
  try { return sessionStorage.getItem(FRESH_AUTH_KEY); } catch { return null; }
}

function reconcileIdentity(userData) {
  try {
    const nextId = userData?.id || null;
    if (!nextId) return;
    const prevId = localStorage.getItem(LAST_USER_KEY);
    if (prevId && prevId !== nextId) {
      clearSessionData();
    }
    localStorage.setItem(LAST_USER_KEY, nextId);
  } catch {
    // localStorage blocked — nothing we can reconcile against.
  }
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Impersonation-v1 (2026-06-21) — install ONE global axios request
// interceptor that, on every outbound request, prefers the impersonation
// token (if present in localStorage) over the regular session token.
// Module-scope so it's installed exactly once even if AuthProvider
// remounts. Returns the interceptor id but we never eject — there's no
// scenario where we want it gone.
//
// Why an interceptor instead of mutating axios.defaults.headers.common:
// defaults are set once at login and don't react to localStorage flips,
// so the impersonation tab (which boots from a URL hand-off after login
// already happened in the original tab) would otherwise send the wrong
// header. The interceptor reads the storage on every request, which is
// cheap and lets the two tabs use different tokens against the same
// axios global.
let _impersonationInterceptorInstalled = false;
function installImpersonationInterceptor() {
  if (_impersonationInterceptorInstalled) return;
  _impersonationInterceptorInstalled = true;
  axios.interceptors.request.use((config) => {
    const imp = getImpersonationToken();
    if (imp) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${imp}`;
    }
    return config;
  });
}
installImpersonationInterceptor();

// Read ?impersonation_token=… and ?impersonation_meta=… off the current
// URL exactly once (the impersonation tab is opened with these query
// params). Returns true if a token was found and stashed. Strips both
// params from the URL via history.replaceState so a refresh of the new
// tab doesn't re-apply the token.
function consumeImpersonationFromURL() {
  try {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("impersonation_token");
    if (!token) return false;
    const metaB64 = params.get("impersonation_meta");
    const meta = decodeImpersonationMeta(metaB64) || {};
    setImpersonation(token, meta);
    params.delete("impersonation_token");
    params.delete("impersonation_meta");
    const qs = params.toString();
    const newUrl =
      window.location.pathname + (qs ? "?" + qs : "") + window.location.hash;
    window.history.replaceState({}, "", newUrl);
    return true;
  } catch {
    return false;
  }
}

export const AuthProvider = ({ children }) => {
  // Consume the impersonation URL BEFORE we read the token state from
  // localStorage so the first fetchUser() call already runs with the
  // impersonation token in the interceptor.
  if (typeof window !== "undefined") {
    consumeImpersonationFromURL();
  }

  const [user, setUser] = useState(null);
  const [token, setToken] = useState(
    () => getImpersonationToken() || localStorage.getItem("token"),
  );
  const [loading, setLoading] = useState(true);
  // HF-Session-Leak-2 — see FRESH_AUTH_KEY above. React state (not just the
  // sessionStorage read) so consumers re-render the instant an explicit
  // login/register/confirm happens, instead of only on next mount.
  const [freshAuthUserId, setFreshAuthUserId] = useState(readFreshAuthUserId);

  const markFreshAuth = (userId) => {
    if (!userId) return;
    try { sessionStorage.setItem(FRESH_AUTH_KEY, String(userId)); } catch { /* blocked */ }
    setFreshAuthUserId(String(userId));
  };
  const clearFreshAuthState = () => {
    try { sessionStorage.removeItem(FRESH_AUTH_KEY); } catch { /* blocked */ }
    setFreshAuthUserId(null);
  };
  // True only when the CURRENT user was authenticated by an explicit action
  // in this tab this session — not merely resumed from a stored token.
  const hasFreshAuth = Boolean(user?.id) && freshAuthUserId === String(user.id);
  // Callers (the /apply identity-confirmation gate) call this once the
  // person has explicitly said "yes, this is me" for a resumed session.
  const confirmIdentity = () => { if (user?.id) markFreshAuth(user.id); };

  useEffect(() => {
    // Cross-tab listener: if a different tab clears the impersonation
    // token (Exit button), or sets a fresh one, re-render so banners and
    // /auth/me pick up the change.
    const onStorage = (ev) => {
      if (ev.key !== IMPERSONATION_KEY) return;
      setToken(getImpersonationToken() || localStorage.getItem("token"));
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // STAFF-SESSIONS-1 — silent renewal. Any click, key or scroll counts as
  // activity; every few minutes, an active tab holding a staff token older
  // than half an hour swaps it for a fresh 12-hour one. Not for view-as tabs:
  // those run on their own one-hour token.
  const lastActivityRef = useRef(Date.now());
  useEffect(() => {
    const mark = () => { lastActivityRef.current = Date.now(); };
    const events = ["pointerdown", "keydown", "scroll", "visibilitychange"];
    events.forEach((e) => window.addEventListener(e, mark, { passive: true }));
    return () => events.forEach((e) => window.removeEventListener(e, mark));
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    const tick = async () => {
      if (getImpersonationToken()) return;
      const current = localStorage.getItem("token");
      if (!shouldRenew(current, { now: Date.now(), lastActivity: lastActivityRef.current })) return;
      try {
        const { data } = await axios.post(`${API}/auth/session/renew`);
        if (data?.token && localStorage.getItem("token") === current) {
          localStorage.setItem("token", data.token);
          axios.defaults.headers.common["Authorization"] = `Bearer ${data.token}`;
          // No setToken(): that would re-run fetchUser for no reason. The
          // interceptor-free default header and localStorage are what every
          // request reads.
        }
      } catch {
        // A 401 here (revoked, too old) is handled by the interceptor below.
      }
    };
    const id = window.setInterval(tick, CHECK_EVERY_MS);
    const onVisible = () => { if (document.visibilityState === "visible") tick(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [user]);

  // STAFF-SESSIONS-1 — when the backend says a staff session is over (12
  // hours idle, signed out everywhere, password changed), sign this tab out
  // cleanly instead of leaving every panel failing. Portal pages keep their
  // own 401 handling (LoanApplicationForm, ClientPortal), so staff only.
  const userRef = useRef(null);
  const logoutRef = useRef(null);
  useEffect(() => { userRef.current = user; }, [user]);
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (r) => r,
      (error) => {
        const status = error?.response?.status;
        const detail = String(error?.response?.data?.detail || "");
        const url = String(error?.config?.url || "");
        const u = userRef.current;
        const staff = u && ["admin", "super_admin", "broker", "junior_broker"].includes(u.role);
        if (
          status === 401 && staff && !getImpersonationToken() && url.startsWith(API)
          && /session expired|token expired|invalid token|sign in again/i.test(detail)
          && !window.__solara_staff_401_handled
        ) {
          window.__solara_staff_401_handled = true;
          setTimeout(() => { window.__solara_staff_401_handled = false; }, 5000);
          toast.info("Your session has ended. Please sign in again.");
          logoutRef.current?.();
        }
        return Promise.reject(error);
      },
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      // HF-Session-Leak-1 — before trusting cached UI state, check whether the
      // identity behind this browser changed since last boot.
      reconcileIdentity(response.data);
      setUser(response.data);
      // PR TIER1-10 — re-identify on boot so a returning session attaches
      // events to the same PostHog profile (without firing a fresh login).
      identifyUser(response.data);
    } catch (error) {
      console.error("Auth error:", error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  // STAFF-MFA-1 (30 Oct 2026) — store a finished sign-in. Shared by password,
  // the staff second step and passkeys. A "remember this device" token, when
  // the server issues one, is kept for the next sign-in.
  const completeSession = (data, method) => {
    const { token: newToken, user: userData } = data;
    if (data.device_token) {
      try { localStorage.setItem(TRUSTED_DEVICE_KEY, data.device_token); } catch { /* blocked */ }
    }
    localStorage.setItem("token", newToken);
    axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    // HF-Session-Leak-2 — this identity was just proven in this tab; safe to
    // hydrate a resumed draft without re-asking.
    markFreshAuth(userData.id);
    // PR TIER1-10 — identify + fire login event.
    identifyUser(userData);
    trackLogin(method);
    return userData;
  };

  // Returns the signed-in user, or, for staff, `{ mfaRequired: true, ticket,
  // next: "code" | "enrol", methods, user }`: the password was right and the
  // caller must now show the second step (LoginPage does).
  const login = async (email, password) => {
    let deviceToken = null;
    try { deviceToken = localStorage.getItem(TRUSTED_DEVICE_KEY); } catch { /* blocked */ }
    const response = await axios.post(`${API}/auth/login`, {
      email, password, ...(deviceToken ? { device_token: deviceToken } : {}),
    });
    if (response.data.success && response.data.mfa_required) {
      return { mfaRequired: true, ...response.data };
    }
    if (response.data.success) {
      return completeSession(response.data, "password");
    }
    throw new Error("Login failed");
  };

  // STAFF-MFA-1 — the second step. `code` is an authenticator or recovery code.
  const verifySecondFactor = async (ticket, code, rememberDevice) => {
    const { data } = await axios.post(`${API}/auth/mfa/verify`, {
      ticket, code, remember_device: !!rememberDevice,
    });
    const userData = completeSession(data, "password+code");
    return { user: userData, recoveryCodesLeft: data.recovery_codes_left };
  };

  const startEnrolment = async (ticket) => {
    const { data } = await axios.post(`${API}/auth/mfa/enrol/start`, { ticket });
    return data;
  };

  const confirmEnrolment = async (ticket, code, rememberDevice) => {
    const { data } = await axios.post(`${API}/auth/mfa/enrol/confirm`, {
      ticket, code, remember_device: !!rememberDevice,
    });
    // Don't store the session yet: the person must see their recovery codes
    // first, and storing it would route them straight into the app.
    return { data, recoveryCodes: data.recovery_codes || [] };
  };

  const finishEnrolment = (data) => completeSession(data, "password+enrol");

  // MAGIC-1 (2026-07-05) — exchange a one-time emailed token for a session.
  // Same storage path as password login; analytics records method=magic_link.
  const loginWithMagicToken = async (magicToken) => {
    const response = await axios.post(`${API}/auth/magic-link/verify`, { token: magicToken });
    if (response.data.success) {
      const { token: newToken, user: userData } = response.data;
      localStorage.setItem("token", newToken);
      axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
      setToken(newToken);
      setUser(userData);
      markFreshAuth(userData.id); // HF-Session-Leak-2
      identifyUser(userData);
      trackLogin("magic_link");
      return userData;
    }
    throw new Error("Sign-in link failed");
  };

  // CLIENT-LINK-1 — the link staff send so a client can finish their own
  // application. Same storage path as the magic link; also returns which
  // application to open and the section to start on.
  const loginWithCompletionLink = async (linkToken) => {
    const response = await axios.post(`${API}/auth/completion-link/verify`, { token: linkToken });
    if (response.data.success) {
      const { token: newToken, user: userData, application_id: applicationId, step } = response.data;
      localStorage.setItem("token", newToken);
      axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
      setToken(newToken);
      setUser(userData);
      markFreshAuth(userData.id);
      identifyUser(userData);
      trackLogin("completion_link");
      return { user: userData, applicationId, step };
    }
    throw new Error("Link failed");
  };

  // DEMO-1d (2026-09-25) — one-click sign-in as a fictional demo user. The
  // route only exists on the demo backend (404 elsewhere). Same storage path
  // as password login.
  const loginWithDemo = async (role) => {
    const response = await axios.post(`${API}/demo/login`, { role });
    if (response.data.success) {
      const { token: newToken, user: userData } = response.data;
      localStorage.setItem("token", newToken);
      axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
      setToken(newToken);
      setUser(userData);
      markFreshAuth(userData.id);
      identifyUser(userData);
      trackLogin("demo");
      return userData;
    }
    throw new Error("Demo sign-in failed");
  };

  // MOB-16 / C3-b (2026-07-14) — sign in with a platform passkey (Face ID /
  // Touch ID). Staff-only server-side; same storage path as password login.
  // The ceremony (options -> navigator.credentials.get -> verify) lives in
  // lib/passkeys; here we just persist the resulting session.
  // STAFF-MFA-1 — with a `ticket`, the passkey is the second step after a
  // password; without one, it's the usernameless Face ID sign-in.
  const loginWithPasskey = async (email, { ticket, rememberDevice } = {}) => {
    const { signInWithPasskey } = await import("@/lib/passkeys");
    const response = await signInWithPasskey(email, { ticket, rememberDevice });
    if (response?.success) {
      return completeSession(response, "passkey");
    }
    throw new Error("Passkey sign-in failed");
  };

  const register = async (data) => {
    const response = await axios.post(`${API}/auth/register`, data);
    if (response.data.success) {
      const { token: newToken, user: userData } = response.data;
      localStorage.setItem("token", newToken);
      axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
      setToken(newToken);
      setUser(userData);
      markFreshAuth(userData.id); // HF-Session-Leak-2 — just registered, in this tab.
      return userData;
    }
    throw new Error("Registration failed");
  };

  const logout = async () => {
    try {
      if (axios.defaults.headers.common["Authorization"]) {
        await axios.post(`${API}/auth/logout`);
      }
    } catch (error) {
      console.error("Logout log error:", error);
    }
    localStorage.removeItem("token");
    // Impersonation-v1 — clear any impersonation token too. The original
    // admin's session token lives in its OWN localStorage key, so this
    // doesn't sign them out — only the impersonation overlay is dropped.
    clearImpersonation();
    // HF-Session-Leak-1 — drop user-scoped localStorage (recent deals, any
    // in-progress application draft) and evict the service worker's cached
    // API responses and downloaded client documents. Neither survived a
    // sign-out before this, so the next person on the machine could read
    // both. Cache eviction is fire-and-forget by design.
    clearSessionData();
    try { localStorage.removeItem(LAST_USER_KEY); } catch { /* blocked */ }
    clearFreshAuthState(); // HF-Session-Leak-2 — don't let the NEXT sign-in on this tab inherit it.
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
    // PR TIER1-10 — drop the PostHog identified-user state so the next
    // session's events don't stick to the previous user's profile.
    resetUser();
  };

  logoutRef.current = logout;

  // STAFF-SESSIONS-1 — end every session for this person on every device.
  const signOutEverywhere = async () => {
    try {
      await axios.post(`${API}/auth/sign-out-everywhere`);
    } finally {
      try { localStorage.removeItem(TRUSTED_DEVICE_KEY); } catch { /* blocked */ }
      delete axios.defaults.headers.common["Authorization"];
      await logout();
    }
  };

  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  // HF-Team-Vis-1 (2026-07-29) — junior_broker is a real staff role
  // (ALLOWED_USER_ROLES / ALLOWED_DEAL_TEAM_ROLES in models.py) but was
  // missing here, so AdminRoute bounced any junior_broker straight to
  // /my-applications (treated like a client) before they ever saw the
  // CRM — no backend fix mattered if this gate never let them in.
  const isStaff =
    user?.role === "admin" ||
    user?.role === "super_admin" ||
    user?.role === "broker" ||
    user?.role === "junior_broker";

  return (
    <AuthContext.Provider value={{ user, token, loading, login, verifySecondFactor, startEnrolment, confirmEnrolment, finishEnrolment, loginWithMagicToken, loginWithCompletionLink, loginWithPasskey, loginWithDemo, register, logout, signOutEverywhere, isAdmin, isStaff, hasFreshAuth, confirmIdentity }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};
